'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { Link } from '@/i18n/routing';
import Logo from '@/components/layout/Logo';
import { Button } from '@/components/ui/Button';
import styles from './page.module.css';

type FormStatus = 'idle' | 'loading' | 'success' | 'error' | 'not_found';

export default function SignInPage() {
  const t = useTranslations('signIn');
  const locale = useLocale();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
      } else if (data.code === 'EMAIL_NOT_FOUND') {
        setStatus('not_found');
      } else if (data.code === 'RATE_LIMITED') {
        setStatus('error');
        setErrorMessage(t('errors.rateLimited', { seconds: data.retryAfter }));
      } else {
        setStatus('error');
        setErrorMessage(data.error || t('errors.generic'));
      }
    } catch {
      setStatus('error');
      setErrorMessage(t('errors.generic'));
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Link href="/" className={styles.logoLink}>
          <Logo />
        </Link>

        <div className={styles.card}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle')}</p>

          {status === 'success' ? (
            <div className={styles.successMessage}>
              <span className={styles.successIcon}>✓</span>
              <h2 className={styles.successTitle}>{t('success.title')}</h2>
              <p className={styles.successText}>{t('success.description')}</p>
            </div>
          ) : status === 'not_found' ? (
            <div className={styles.notFoundMessage}>
              <span className={styles.notFoundIcon}>📧</span>
              <h2 className={styles.notFoundTitle}>{t('notFound.title')}</h2>
              <p className={styles.notFoundText}>{t('notFound.description')}</p>
              <Link href="/" className={styles.ctaLink}>
                <Button size="md">{t('notFound.cta')}</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <label htmlFor="email" className={styles.label}>
                  {t('emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  className={styles.input}
                  required
                  disabled={status === 'loading'}
                />
              </div>

              {status === 'error' && errorMessage && (
                <div className={styles.errorMessage}>{errorMessage}</div>
              )}

              <Button
                type="submit"
                size="lg"
                className={styles.submitButton}
                disabled={status === 'loading' || !email}
              >
                {status === 'loading' ? t('sending') : t('submit')}
              </Button>

              <p className={styles.hint}>{t('hint')}</p>
            </form>
          )}
        </div>

        <div className={styles.newHereCard}>
          <h2 className={styles.newHereTitle}>{t('newHereTitle')}</h2>
          <p className={styles.newHereDescription}>{t('newHereDescription')}</p>
          <Link href="/" className={styles.ctaLink}>
            <Button size="md" variant="secondary">{t('newHereCta')}</Button>
          </Link>
        </div>

        <Link href="/" className={styles.backLink}>
          ← {t('backToHome')}
        </Link>
      </div>
    </div>
  );
}
